import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { storageApi, StorageLocation, TrashBatch } from '../../api/storage.api';
import { MapPin, Plus, Grid3X3, ArrowLeft, PackagePlus, Printer, ToggleLeft, ToggleRight, Trash2, Pencil, Check, X, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { hardwareBridgeZebra } from '../../services/printers/HardwareBridgeAdapter';

// ═══════════════════════════════════════════════════════════
// Génération ZPL pour étiquettes d'emplacement
//
// Le gabarit était figé sur 100×150 mm. Sur un rouleau plus court — le 60×50
// que porte la Zebra la plupart du temps — le QR sortait seul et TOUT le texte
// tombait hors de l'étiquette : on obtenait des autocollants muets, impossibles
// à lire à l'œil et impossibles à retrouver si le QR s'abîme.
//
// Les positions sont donc calculées à partir du format choisi, jamais écrites en
// dur. Deux mises en page, parce qu'une étiquette de 25 mm de haut ne peut pas
// porter la même chose qu'une de 150 : QR au-dessus du texte quand la hauteur le
// permet, QR à gauche et texte à droite sur les petites étiquettes larges.
// ═══════════════════════════════════════════════════════════

const DPI = 203;

/** Rouleaux réellement utilisés à la boutique (cf. modèles d'étiquettes). */
export const LABEL_FORMATS = [
  { key: '50x25', label: '50 × 25 mm', widthMm: 50, heightMm: 25 },
  { key: '60x50', label: '60 × 50 mm', widthMm: 60, heightMm: 50 },
  { key: '100x150', label: '100 × 150 mm', widthMm: 100, heightMm: 150 },
  { key: '25x50', label: '25 × 50 mm', widthMm: 25, heightMm: 50 },
] as const;

export type LabelFormat = (typeof LABEL_FORMATS)[number];

/** Le rouleau est une propriété de LA MACHINE, pas des données : il se retient
 *  par poste, pas en base. */
const FORMAT_STORAGE_KEY = 'storage_label_format';

export function loadLabelFormat(): LabelFormat {
  try {
    const key = localStorage.getItem(FORMAT_STORAGE_KEY);
    return LABEL_FORMATS.find((f) => f.key === key) ?? LABEL_FORMATS[0];
  } catch {
    return LABEL_FORMATS[0];
  }
}

function mmToDots(mm: number): number {
  return Math.round((mm * DPI) / 25.4);
}

/**
 * Ramène un nom libre à de l'ASCII pur avant impression.
 *
 * Les libellés déjà imprimés évitent les accents (« Allee », « Etag ») : un nom
 * saisi librement (« Réserve épicerie ») ne doit pas dépendre de l'encodage du
 * chemin d'impression pour rester lisible. La décomposition Unicode ne suffit
 * pas — les ligatures (œ, æ) et les tirets typographiques n'ont pas de forme
 * décomposée et passeraient tels quels.
 */
function toPrintable(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/œ/g, 'oe').replace(/Œ/g, 'OE')
    .replace(/æ/g, 'ae').replace(/Æ/g, 'AE')
    .replace(/[–—]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[^\x20-\x7E]/g, '');
}

/**
 * Nombre de modules du QR, pour dimensionner l'agrandissement.
 * Le contenu est « LOC: » + un uuid = 40 caractères, ce qui donne une version 4
 * à 6 en correction Q — soit 33 à 41 modules. On prend le haut de la
 * fourchette : sous-estimer déborde de l'étiquette, surestimer laisse une marge.
 */
const QR_MODULES = 41;

/**
 * Hauteur de police tenant dans une largeur donnée.
 *
 * En ^A0, un caractère est large d'environ 0,82 fois sa hauteur. Dimensionner
 * une police sur la seule hauteur de l'étiquette — ce que je faisais — déborde
 * dès qu'elle est étroite : « A01.C01.E01 » en corps 60 réclame 540 points de
 * large, une étiquette de 25 mm n'en offre que 200.
 */
const RATIO_CARACTERE = 0.82;
function hauteurPolice(texte: string, largeurDispo: number, plafond: number): number {
  const parLargeur = Math.floor(largeurDispo / Math.max(1, texte.length * RATIO_CARACTERE));
  return Math.max(8, Math.min(plafond, parLargeur));
}

const deuxChiffres = (n: number) => String(n).padStart(2, '0');

function generateLocationLabelZPL(loc: StorageLocation, format: LabelFormat): string {
  const w = mmToDots(format.widthMm);
  const h = mmToDots(format.heightMm);
  const marge = Math.round(Math.min(w, h) * 0.06);
  const utile = w - 2 * marge;

  const aisleLabel = loc.aisle_name ? toPrintable(loc.aisle_name) : `Allee ${loc.aisle}`;
  const sousTitre = `${aisleLabel} / Col ${loc.column} / Etag ${loc.shelf}`;
  const nom = loc.name?.trim() ? toPrintable(loc.name.trim()) : null;

  let zpl = `^XA\n^CI28\n^PW${w}\n^LL${h}\n^MMT\n~SD25\n`;

  // Étiquette nettement plus large que haute : QR à gauche, texte à droite.
  if (format.heightMm < format.widthMm * 0.7) {
    const mag = Math.max(2, Math.floor((h - 2 * marge) / QR_MODULES));
    const qrTaille = mag * QR_MODULES;
    zpl += `^FO${marge},${Math.round((h - qrTaille) / 2)}\n^BQN,2,${mag},Q,7\n^FDQA,${loc.qr_code}^FS\n`;

    // Les trois numéros EMPILÉS à droite du QR, pas « A01.C01.E01 » sur une
    // ligne : le QR mange 21 des 50 mm, il en reste 25 pour onze caractères,
    // soit 2,3 mm chacun — illisible à bout de bras. Empilés, ce sont trois
    // caractères par ligne, donc trois fois plus gros.
    const xTexte = marge * 2 + qrTaille;
    const largeurTexte = w - xTexte - marge;
    const lignes = [`A${deuxChiffres(loc.aisle)}`, `C${deuxChiffres(loc.column)}`, `E${deuxChiffres(loc.shelf)}`];
    const inter = Math.round(marge * 0.3);
    const hLigne = Math.min(
      hauteurPolice('A00', largeurTexte, 999),
      Math.floor((h - 2 * marge - 2 * inter) / 3),
    );
    let yTexte = Math.round((h - (3 * hLigne + 2 * inter)) / 2);
    for (const ligne of lignes) {
      zpl += `^FO${xTexte},${yTexte}\n^A0N,${hLigne},${Math.round(hLigne * RATIO_CARACTERE)}\n`;
      zpl += `^FB${largeurTexte},1,0,L,0\n^FD${ligne}^FS\n`;
      yTexte += hLigne + inter;
    }
    zpl += '^PQ1,0,1,Y\n^XZ\n';
    return zpl;
  }

  // Le QR d'abord, en taille : c'est lui qui a un plancher de lisibilité (41
  // modules, il ne faut pas descendre sous ~0,5 mm par module). Le texte prend
  // ce qui reste — mais il est IMPRIMÉ EN PREMIER, en haut : quand le gabarit
  // dépasse la hauteur réelle du rouleau, c'est le bas qui se perd.
  const qrTaille = Math.max(2, Math.floor(Math.min(utile, h * 0.45) / QR_MODULES)) * QR_MODULES;
  const mag = qrTaille / QR_MODULES;
  const zoneTexte = h - qrTaille - 3 * marge;

  let y = marge;

  if (format.widthMm < 35) {
    // Étiquette étroite : « A01.C01.E01 » sur une ligne y serait minuscule. Les
    // trois numéros sont empilés — c'est ce qu'on lit de loin dans la réserve.
    const lignes = [`A${deuxChiffres(loc.aisle)}`, `C${deuxChiffres(loc.column)}`, `E${deuxChiffres(loc.shelf)}`];
    const inter = Math.round(marge * 0.35);
    const hLigne = Math.min(
      hauteurPolice('A00', utile, 999),
      Math.floor((zoneTexte - 2 * inter) / 3),
    );
    for (const ligne of lignes) {
      zpl += `^FO0,${y}\n^A0N,${hLigne},${Math.round(hLigne * RATIO_CARACTERE)}\n^FB${w},1,0,C,0\n^FD${ligne}^FS\n`;
      y += hLigne + inter;
    }
  } else {
    const hCode = hauteurPolice(loc.label, utile, Math.round(h * 0.15));
    zpl += `^FO0,${y}\n^A0N,${hCode},${Math.round(hCode * RATIO_CARACTERE)}\n^FB${w},1,0,C,0\n^FD${loc.label}^FS\n`;
    y += hCode + Math.round(marge * 0.4);

    const hSous = hauteurPolice(sousTitre, utile, Math.round(h * 0.072));
    zpl += `^FO0,${y}\n^A0N,${hSous},${Math.round(hSous * RATIO_CARACTERE)}\n^FB${w},1,0,C,0\n^FD${sousTitre}^FS\n`;
    y += hSous + Math.round(marge * 0.3);

    if (nom) {
      const hNom = hauteurPolice(nom, utile, Math.round(h * 0.072));
      zpl += `^FO0,${y}\n^A0N,${hNom},${Math.round(hNom * RATIO_CARACTERE)}\n^FB${w},1,0,C,0\n^FD${nom}^FS\n`;
      y += hNom + Math.round(marge * 0.3);
    }
  }

  zpl += `^FO${Math.round((w - qrTaille) / 2)},${h - qrTaille - marge}\n^BQN,2,${mag},Q,7\n^FDQA,${loc.qr_code}^FS\n`;
  zpl += '^PQ1,0,1,Y\n^XZ\n';
  return zpl;
}

/**
 * Découpe une allée en colonnes, chacune ordonnée de la plus haute étagère à la
 * plus basse.
 *
 * Une colonne est une travée du rayonnage : physiquement une PILE. L'afficher
 * comme une suite horizontale (ce que faisait la grille précédente) ne
 * correspondait à rien de ce qu'on a sous les yeux dans la réserve. L'étagère 1
 * est en bas, comme on numérote depuis le sol.
 *
 * Copie avant tri : `sort` modifie le tableau en place, et celui-ci vient de
 * l'état React.
 */
function columnsOf(aisleLocs: StorageLocation[]): Array<{ column: number; cells: StorageLocation[] }> {
  const byColumn = new Map<number, StorageLocation[]>();
  for (const loc of aisleLocs) {
    const list = byColumn.get(loc.column) ?? [];
    list.push(loc);
    byColumn.set(loc.column, list);
  }
  return Array.from(byColumn.entries())
    .sort(([a], [b]) => a - b)
    .map(([column, cells]) => ({
      column,
      cells: cells.slice().sort((a, b) => b.shelf - a.shelf),
    }));
}

export default function StorageLocationsPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [expandedLocation, setExpandedLocation] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState(false);
  const [batchActing, setBatchActing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /**
   * Formulaire de création en lot.
   *
   * Les bornes sont `number | ''` et NON `number` : un champ contrôlé qui
   * reconvertit le vide en 1 à chaque frappe ne peut pas être vidé pour être
   * retapé — effacer « 1 » le fait réapparaître, et le chiffre suivant se colle
   * derrière (on voulait 2, on obtenait 12). Le vide est un état de saisie
   * légitime ; il n'est traduit en nombre qu'à l'envoi.
   */
  const [batchForm, setBatchForm] = useState<{
    aisleFrom: number | ''; aisleTo: number | '';
    columnFrom: number | ''; columnTo: number | '';
    shelfFrom: number | ''; shelfTo: number | '';
    aisleName: string;
  }>({
    aisleFrom: 1, aisleTo: 1,
    columnFrom: 1, columnTo: 5,
    shelfFrom: 1, shelfTo: 4,
    aisleName: '',
  });
  const [creating, setCreating] = useState(false);

  // Édition d'un emplacement (adresse, nom, note)
  const [editing, setEditing] = useState<StorageLocation | null>(null);
  // Renommage d'allée : numéro en cours d'édition + saisie
  const [renamingAisle, setRenamingAisle] = useState<number | null>(null);
  const [aisleNameDraft, setAisleNameDraft] = useState('');
  // Étiquettes devenues fausses après une insertion, à réimprimer
  const [relabelled, setRelabelled] = useState<StorageLocation[] | null>(null);
  // Format du rouleau chargé dans la Zebra — propriété du POSTE, pas des données
  const [labelFormat, setLabelFormat] = useState<LabelFormat>(loadLabelFormat);
  // Corbeille : suppressions annulables des 30 derniers jours
  const [trash, setTrash] = useState<TrashBatch[]>([]);
  const [showTrash, setShowTrash] = useState(false);

  useEffect(() => {
    if (tenant?.id) loadLocations();
  }, [tenant?.id]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await storageApi.getLocations();
      setLocations(data);
      storageApi.getTrash().then(setTrash).catch(() => setTrash([]));
    } catch (err) {
      toast.error('Erreur lors du chargement des emplacements');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Annule une suppression. Les emplacements reviennent avec leur QR d'origine,
   * donc les étiquettes déjà collées répondent de nouveau au scan.
   * Une adresse réoccupée depuis ne peut pas revenir : on le dit au lieu de la
   * renommer en douce.
   */
  const handleRestore = async (batch: TrashBatch) => {
    setBatchActing(true);
    try {
      const res = await storageApi.restoreFromTrash(batch.ids);
      if (res.restored > 0) {
        toast.success(`${res.restored} emplacement(s) restauré(s) — leurs étiquettes refonctionnent`);
      }
      if (res.conflicts.length > 0) {
        toast.warning(
          `${res.conflicts.length} non restauré(s), leur adresse est réoccupée : ${res.conflicts.slice(0, 5).join(', ')}`
          + (res.conflicts.length > 5 ? '…' : ''),
          { duration: 10000 },
        );
      }
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la restauration');
    } finally {
      setBatchActing(false);
    }
  };

  /** Un champ vidé vaut 1 à l'envoi — mais il reste vide tant qu'on tape. */
  const borne = (v: number | ''): number => (v === '' ? 1 : v);

  /** Ouvre le formulaire sur la PROCHAINE allée libre : neuf fois sur dix, on
   *  vient y créer une allée de plus, pas réécrire celles qui existent. */
  const openBatchForm = () => {
    const derniere = locations.reduce((max, l) => Math.max(max, l.aisle), 0);
    setBatchForm(f => ({ ...f, aisleFrom: derniere + 1, aisleTo: derniere + 1, aisleName: '' }));
    setShowBatchForm(true);
  };

  const handleBatchCreate = async () => {
    setCreating(true);
    try {
      const result = await storageApi.createLocationsBatch({
        aisleFrom: borne(batchForm.aisleFrom), aisleTo: borne(batchForm.aisleTo),
        columnFrom: borne(batchForm.columnFrom), columnTo: borne(batchForm.columnTo),
        shelfFrom: borne(batchForm.shelfFrom), shelfTo: borne(batchForm.shelfTo),
        aisleName: batchForm.aisleName,
      });

      // « 0 emplacement(s) créé(s) » ne disait pas POURQUOI, et laissait croire
      // à une panne alors que l'allée existait simplement déjà. C'est ce
      // malentendu qui a conduit à un « nettoyage » manuel le 25/08.
      if (result.created === 0 && (result.skipped ?? 0) > 0) {
        toast.warning(
          `Rien de créé : ces ${result.skipped} emplacements existent déjà. `
          + 'Vérifiez le numéro d\'allée — il n\'y a rien à corriger ici.',
          { duration: 9000 },
        );
      } else if ((result.skipped ?? 0) > 0) {
        toast.success(`${result.created} emplacement(s) créé(s) — ${result.skipped} existaient déjà`);
      } else {
        toast.success(`${result.created} emplacement(s) créé(s)`);
      }

      // Une sélection qui survit à une création est une arme chargée : la barre
      // d'actions reste ouverte sur des emplacements qu'on ne regarde plus.
      setSelected(new Set());
      setShowDeleteConfirm(false);
      setShowBatchForm(false);
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  // ── Édition libre de la carte ─────────────────────────────

  /**
   * Enregistre les modifications d'un emplacement.
   *
   * Déplacer une adresse change le code imprimé mais pas le QR : l'étiquette
   * collée mène toujours au bon endroit, elle ment seulement sur le code. On le
   * dit, et on propose de réimprimer — sinon la réserve se remplit d'étiquettes
   * qui se contredisent.
   */
  const handleSaveLocation = async (
    form: { aisle: number; column: number; shelf: number; name: string; notes: string; active: boolean },
  ) => {
    if (!editing) return;
    try {
      const updated = await storageApi.updateLocation(editing.id, {
        aisle: form.aisle,
        column: form.column,
        shelf: form.shelf,
        name: form.name.trim() || null,
        notes: form.notes,
        active: form.active,
      });
      toast.success(`${updated.label} enregistré`);
      if (updated.label_changed) {
        toast.warning(
          `${updated.label_changed} est devenu ${updated.label} — réimprimez son étiquette`,
          { duration: 8000 },
        );
      }
      setEditing(null);
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  /** Supprime un emplacement isolé (refusé par le serveur s'il contient du stock). */
  const handleDeleteOne = async (loc: StorageLocation) => {
    const ok = window.confirm(`Supprimer définitivement l'emplacement ${loc.label} ?`);
    if (!ok) return;
    try {
      await storageApi.deleteLocationsBatch([loc.id]);
      toast.success(`${loc.label} supprimé`);
      setEditing(null);
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  /** Nomme une allée (« Frigo ») : le numéro reste, les mots s'ajoutent. */
  const handleRenameAisle = async (number: number) => {
    try {
      await storageApi.setAisleName(number, { name: aisleNameDraft });
      toast.success(aisleNameDraft.trim() ? `Allée ${number} : « ${aisleNameDraft.trim()} »` : `Nom retiré de l'allée ${number}`);
      setRenamingAisle(null);
      setAisleNameDraft('');
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors du renommage');
    }
  };

  // ── Impression ────────────────────────────────────────────

  const handlePrintOne = async (loc: StorageLocation) => {
    setPrinting(true);
    try {
      const zpl = generateLocationLabelZPL(loc, labelFormat);
      const result = await hardwareBridgeZebra.printZPL(zpl);
      if (result.success) {
        toast.success(`Étiquette ${loc.label} imprimée`);
      } else {
        toast.error(`Erreur : ${result.message}`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Erreur d\'impression — vérifiez le bridge Zebra');
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintSelected = async () => {
    if (selected.size === 0) return;
    const toPrint = locations.filter(l => selected.has(l.id));
    setPrinting(true);
    let printed = 0;
    try {
      for (const loc of toPrint) {
        const zpl = generateLocationLabelZPL(loc, labelFormat);
        const result = await hardwareBridgeZebra.printZPL(zpl);
        if (result.success) printed++;
      }
      toast.success(`${printed}/${toPrint.length} étiquette(s) imprimée(s)`);
      setSelected(new Set());
    } catch (err: any) {
      toast.error(err.message || 'Erreur d\'impression');
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintAisle = async (aisleLocs: StorageLocation[]) => {
    setPrinting(true);
    let printed = 0;
    try {
      for (const loc of aisleLocs) {
        const zpl = generateLocationLabelZPL(loc, labelFormat);
        const result = await hardwareBridgeZebra.printZPL(zpl);
        if (result.success) printed++;
      }
      toast.success(`${printed}/${aisleLocs.length} étiquette(s) imprimée(s)`);
    } catch (err: any) {
      toast.error(err.message || 'Erreur d\'impression');
    } finally {
      setPrinting(false);
    }
  };

  const toggleSelect = (locId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(locId)) next.delete(locId);
      else next.add(locId);
      return next;
    });
  };

  /** Sélectionne ou désélectionne d'un coup un groupe (une allée, une colonne). */
  const toggleSelectGroup = (groupLocs: StorageLocation[]) => {
    setSelected(prev => {
      const next = new Set(prev);
      const allSelected = groupLocs.every(l => next.has(l.id));
      if (allSelected) {
        groupLocs.forEach(l => next.delete(l.id));
      } else {
        groupLocs.forEach(l => next.add(l.id));
      }
      return next;
    });
  };

  /**
   * Ajoute une étagère au-dessus de la plus haute de cette colonne.
   *
   * C'est le geste réel : on ne « crée pas un emplacement » en tapant trois
   * nombres, on rajoute une planche à une colonne qu'on a sous les yeux.
   */
  const handleAddShelf = async (aisle: number, column: number, cells: StorageLocation[]) => {
    const nextShelf = Math.max(...cells.map(c => c.shelf)) + 1;
    try {
      const created = await storageApi.createLocation({ aisle, column, shelf: nextShelf });
      toast.success(`Étagère ${nextShelf} ajoutée — imprimez l'étiquette ${created.label}`);
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'ajout');
    }
  };

  /**
   * Intercale une étagère à ce niveau : celles du dessus montent d'un cran.
   *
   * Leur code change donc, mais pas leur QR : la marchandise ne bouge pas, ce
   * sont les étiquettes collées qui deviennent fausses. On les retient pour les
   * faire réimprimer d'un geste, sans quoi la réserve se retrouve avec des
   * codes qui se contredisent.
   */
  const handleInsertShelf = async (aisle: number, column: number, shelf: number) => {
    try {
      const { created, renamed } = await storageApi.insertShelf({ aisle, column, shelf });
      const fresh = await storageApi.getLocations();
      setLocations(fresh);
      if (renamed.length === 0) {
        toast.success(`${created.label} créée — imprimez son étiquette`);
      } else {
        toast.success(`${created.label} intercalée — ${renamed.length} étiquette(s) à réimprimer`);
        setRelabelled(fresh.filter(l => renamed.some(r => r.id === l.id) || l.id === created.id));
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'insertion');
    }
  };

  /** Réimprime les étiquettes devenues fausses après une insertion. */
  const handleReprintRelabelled = async () => {
    if (!relabelled?.length) return;
    setPrinting(true);
    let printed = 0;
    try {
      for (const loc of relabelled) {
        const result = await hardwareBridgeZebra.printZPL(generateLocationLabelZPL(loc, labelFormat));
        if (result.success) printed++;
      }
      toast.success(`${printed}/${relabelled.length} étiquette(s) réimprimée(s)`);
      if (printed === relabelled.length) setRelabelled(null);
    } catch (err: any) {
      toast.error(err.message || 'Erreur d\'impression');
    } finally {
      setPrinting(false);
    }
  };

  /**
   * Ajoute une colonne à la suite, aussi haute que la plus haute de l'allée :
   * les travées d'un même rayonnage ont presque toujours le même nombre de
   * planches. On peut ensuite en retirer ou en ajouter une.
   */
  const handleAddColumn = async (aisle: number, aisleLocs: StorageLocation[]) => {
    const nextColumn = Math.max(...aisleLocs.map(l => l.column)) + 1;
    const shelfCount = Math.max(...aisleLocs.map(l => l.shelf));
    try {
      const result = await storageApi.createLocationsBatch({
        aisleFrom: aisle, aisleTo: aisle,
        columnFrom: nextColumn, columnTo: nextColumn,
        shelfFrom: 1, shelfTo: shelfCount,
      });
      toast.success(`Colonne ${nextColumn} ajoutée — ${result.created} étagère(s) à étiqueter`);
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'ajout');
    }
  };

  // ── Actions batch ────────────────────────────────────────

  const handleBatchActivate = async (active: boolean) => {
    if (selected.size === 0) return;
    setBatchActing(true);
    try {
      const result = await storageApi.updateLocationsBatch(Array.from(selected), { active });
      toast.success(`${result.updated} emplacement(s) ${active ? 'activé(s)' : 'désactivé(s)'}`);
      setSelected(new Set());
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setBatchActing(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selected.size === 0) return;
    setBatchActing(true);
    try {
      const result = await storageApi.deleteLocationsBatch(Array.from(selected));
      toast.success(`${result.deleted} emplacement(s) supprimé(s)`);
      setSelected(new Set());
      setShowDeleteConfirm(false);
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
      setShowDeleteConfirm(false);
    } finally {
      setBatchActing(false);
    }
  };

  /**
   * Vider un emplacement retire la carte, pas la marchandise : le stock reste
   * en réserve, simplement « non rangé ». On le dit dans la confirmation, sans
   * quoi le geste passerait pour une perte de stock.
   */
  const handleClearLocation = async (loc: StorageLocation) => {
    const count = loc.assignments?.length || 0;
    const ok = window.confirm(
      `Vider ${loc.label} ?\n\n${count} rangement(s) y sont enregistrés. `
      + 'Le stock n\'est PAS supprimé : il redevient simplement « non rangé » et pourra être rerangé.',
    );
    if (!ok) return;
    try {
      const res = await storageApi.clearLocation(loc.id);
      toast.success(`${res.label} vidé (${res.cleared} rangement(s) retiré(s))`);
      loadLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors du vidage');
    }
  };

  const expectedCount =
    (borne(batchForm.aisleTo) - borne(batchForm.aisleFrom) + 1) *
    (borne(batchForm.columnTo) - borne(batchForm.columnFrom) + 1) *
    (borne(batchForm.shelfTo) - borne(batchForm.shelfFrom) + 1);

  /**
   * Emplacements déjà présents dans la plage d'allées visée. Le dire AVANT
   * d'envoyer : recréer par-dessus une allée existante ne crée rien, et ce
   * silence a déjà mené à une suppression de rattrapage.
   */
  const dejaPresents = locations.filter(
    (l) => l.aisle >= borne(batchForm.aisleFrom) && l.aisle <= borne(batchForm.aisleTo),
  ).length;

  // Grouper par allée
  const aisleGroups = locations.reduce<Record<number, StorageLocation[]>>((acc, loc) => {
    if (!acc[loc.aisle]) acc[loc.aisle] = [];
    acc[loc.aisle].push(loc);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header standalone */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-h-[48px]"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Grid3X3 className="w-6 h-6 text-purple-600" />
          <h1 className="text-lg font-bold text-gray-900">Emplacements</h1>
          <span className="text-sm text-gray-400">{locations.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/storage/putaway')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors min-h-[48px]"
          >
            <PackagePlus className="w-4 h-4" />
            Rangement
          </button>
          {/* Le rouleau chargé dans la Zebra : sans ce choix, le gabarit était
              figé sur 100×150 et le texte tombait hors des étiquettes plus
              courtes — on obtenait des autocollants muets. */}
          <select
            value={labelFormat.key}
            onChange={(e) => {
              const f = LABEL_FORMATS.find((x) => x.key === e.target.value) ?? LABEL_FORMATS[0];
              setLabelFormat(f);
              try { localStorage.setItem('storage_label_format', f.key); } catch { /* mode privé */ }
              toast.success(`Étiquettes ${f.label} — réglage propre à ce poste`);
            }}
            className="px-2 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 min-h-[48px]"
            title="Format du rouleau chargé dans l'imprimante"
          >
            {LABEL_FORMATS.map((f) => (
              <option key={f.key} value={f.key}>Étiquettes {f.label}</option>
            ))}
          </select>

          {trash.length > 0 && (
            <button
              onClick={() => setShowTrash(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors min-h-[48px]"
              title="Annuler une suppression récente"
            >
              <Undo2 className="w-4 h-4" />
              Corbeille
              <span className="px-1.5 rounded-full bg-amber-200 text-[11px] font-bold">
                {trash.reduce((n, b) => n + b.count, 0)}
              </span>
            </button>
          )}
          <button
            onClick={() => (showBatchForm ? setShowBatchForm(false) : openBatchForm())}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 min-h-[48px]"
          >
            <Plus className="w-4 h-4" />
            Créer en lot
          </button>
        </div>
      </header>

      {/* Barre d'actions batch (visible quand sélection) */}
      {selected.size > 0 && (
        <div className="bg-purple-50 border-b border-purple-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
          <span className="text-sm font-medium text-purple-800">
            {selected.size} emplacement{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrintSelected}
              disabled={printing || batchActing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
            >
              <Printer className="w-3.5 h-3.5" />
              {printing ? 'Impression...' : 'Imprimer QR'}
            </button>
            <button
              onClick={() => handleBatchActivate(true)}
              disabled={batchActing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
            >
              <ToggleRight className="w-3.5 h-3.5" />
              Activer
            </button>
            <button
              onClick={() => handleBatchActivate(false)}
              disabled={batchActing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
            >
              <ToggleLeft className="w-3.5 h-3.5" />
              Désactiver
            </button>
            {/* La confirmation était ici un bouton « Confirmer » qui remplaçait
                « Supprimer » AU MÊME PIXEL : deux clics au même endroit
                détruisaient tout, et rien ne disait jamais combien. 221
                emplacements ont disparu comme ça le 25/08. Elle passe en
                fenêtre, qui énonce ce qu'elle va détruire. */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={batchActing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>
            <button
              onClick={() => { setSelected(new Set()); setShowDeleteConfirm(false); }}
              className="ml-2 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
            >
              Tout déselectionner
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
      <div className="p-6 max-w-6xl mx-auto">

      {/* Après une insertion, les étagères du dessus ont changé de code : leurs
          étiquettes collées mentent jusqu'à réimpression. On dit lesquelles et
          on les réimprime d'un geste, plutôt que de laisser l'opérateur les
          retrouver. */}
      {relabelled && relabelled.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-900">
            {relabelled.length} étiquette{relabelled.length > 1 ? 's ont' : ' a'} changé de code
          </p>
          <p className="text-xs text-amber-800 mt-1 font-mono">
            {relabelled.map(l => l.label).join(' · ')}
          </p>
          <p className="text-xs text-amber-700 mt-1">
            La marchandise n'a pas bougé (le QR est inchangé) — seules les étiquettes collées sont à refaire.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleReprintRelabelled}
              disabled={printing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
            >
              <Printer className="w-3.5 h-3.5" />
              {printing ? 'Impression...' : `Réimprimer ces ${relabelled.length} étiquettes`}
            </button>
            <button
              onClick={() => setRelabelled(null)}
              className="px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-100 rounded-lg"
            >
              Plus tard
            </button>
          </div>
        </div>
      )}


      {/* Formulaire de création en lot */}
      {showBatchForm && (
        <div className="mb-6 p-4 bg-white rounded-lg border shadow-sm">
          <h3 className="font-medium mb-3">Créer des emplacements en lot</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Allées</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" value={batchForm.aisleFrom}
                  onChange={e => setBatchForm(f => ({ ...f, aisleFrom: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                  className="w-16 px-2 py-1.5 border rounded text-sm"
                />
                <span className="text-gray-400">à</span>
                <input
                  type="number" min="1" value={batchForm.aisleTo}
                  onChange={e => setBatchForm(f => ({ ...f, aisleTo: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                  className="w-16 px-2 py-1.5 border rounded text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Colonnes</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" value={batchForm.columnFrom}
                  onChange={e => setBatchForm(f => ({ ...f, columnFrom: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                  className="w-16 px-2 py-1.5 border rounded text-sm"
                />
                <span className="text-gray-400">à</span>
                <input
                  type="number" min="1" value={batchForm.columnTo}
                  onChange={e => setBatchForm(f => ({ ...f, columnTo: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                  className="w-16 px-2 py-1.5 border rounded text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Étagères</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" value={batchForm.shelfFrom}
                  onChange={e => setBatchForm(f => ({ ...f, shelfFrom: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                  className="w-16 px-2 py-1.5 border rounded text-sm"
                />
                <span className="text-gray-400">à</span>
                <input
                  type="number" min="1" value={batchForm.shelfTo}
                  onChange={e => setBatchForm(f => ({ ...f, shelfTo: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                  className="w-16 px-2 py-1.5 border rounded text-sm"
                />
              </div>
            </div>
          </div>
          {/* Nommer l'allée pendant qu'on la crée : c'est le moment où l'on sait
              ce qu'elle est. Une seule allée à la fois, sinon le nom coifferait
              des allées différentes. */}
          {borne(batchForm.aisleFrom) === borne(batchForm.aisleTo) && (
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Nom de l'allée {borne(batchForm.aisleFrom)} (facultatif)
              </label>
              <input
                type="text"
                value={batchForm.aisleName}
                onChange={e => setBatchForm(f => ({ ...f, aisleName: e.target.value }))}
                placeholder="Frigo, Réserve épicerie, Mezzanine…"
                className="w-full max-w-sm px-3 py-2 border rounded text-sm"
              />
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="text-gray-500">
                {expectedCount} emplacement{expectedCount !== 1 ? 's' : ''} sera/seront créé(s)
              </p>
              {dejaPresents > 0 && (
                <p className="text-amber-700 mt-0.5">
                  ⚠️ L'allée {borne(batchForm.aisleFrom)}
                  {borne(batchForm.aisleTo) !== borne(batchForm.aisleFrom) ? ` à ${borne(batchForm.aisleTo)}` : ''}
                  {' '}contient déjà {dejaPresents} emplacement{dejaPresents > 1 ? 's' : ''} :
                  ceux-là seront conservés, pas recréés.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBatchForm(false)}
                className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleBatchCreate}
                disabled={creating || expectedCount <= 0}
                className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des emplacements groupés par allée */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      ) : locations.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg text-gray-500">Aucun emplacement configuré</p>
          <p className="text-sm text-gray-400 mt-1">Créez des emplacements en lot pour commencer</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(aisleGroups)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([aisle, locs]) => {
              const allInAisleSelected = locs.every(l => selected.has(l.id));
              return (
                <div key={aisle} className="bg-white rounded-lg border overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b font-medium flex items-center gap-2 flex-wrap">
                    <input
                      type="checkbox"
                      checked={allInAisleSelected}
                      onChange={() => toggleSelectGroup(locs)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <MapPin className="w-4 h-4 text-purple-500" />
                    {renamingAisle === Number(aisle) ? (
                      <form
                        onSubmit={(e) => { e.preventDefault(); handleRenameAisle(Number(aisle)); }}
                        className="flex items-center gap-1.5"
                      >
                        <span className="text-gray-500">Allée {aisle}</span>
                        <input
                          value={aisleNameDraft}
                          onChange={(e) => setAisleNameDraft(e.target.value)}
                          placeholder="Frigo, Réserve épicerie…"
                          autoFocus
                          className="px-2 py-1 border border-purple-300 rounded text-sm w-52"
                        />
                        <button type="submit" className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Enregistrer">
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRenamingAisle(null); setAisleNameDraft(''); }}
                          className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                          title="Annuler"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </form>
                    ) : (
                      <>
                        {locs[0]?.aisle_name ? (
                          <span>
                            {locs[0].aisle_name}
                            <span className="text-xs text-gray-400 font-normal ml-1.5">(allée {aisle})</span>
                          </span>
                        ) : (
                          <span>Allée {aisle}</span>
                        )}
                        <button
                          onClick={() => {
                            setRenamingAisle(Number(aisle));
                            setAisleNameDraft(locs[0]?.aisle_name || '');
                          }}
                          className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="Nommer cette allée"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    <span className="text-xs text-gray-400 ml-2">{locs.length} emplacements</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePrintAisle(locs); }}
                      disabled={printing}
                      className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                      title={`Imprimer les ${locs.length} étiquettes de l'allée ${aisle}`}
                    >
                      <Printer className="w-3.5 h-3.5" />
                      Imprimer allée
                    </button>
                  </div>
                  {/* Une colonne = une travée du rayonnage, donc une PILE d'étagères.
                      L'écran doit ressembler à ce qu'on a devant les yeux dans la
                      réserve : les colonnes côte à côte, et dans chacune l'étagère 1
                      en BAS, comme on numérote depuis le sol. */}
                  <div className="p-3 overflow-x-auto">
                    <div className="flex gap-3 items-start">
                      {columnsOf(locs).map(({ column, cells }) => {
                        const allInColumnSelected = cells.every(l => selected.has(l.id));
                        return (
                          <div key={column} className="shrink-0 w-52">
                            <div className="flex items-center gap-1.5 px-1 pb-1.5 mb-2 border-b">
                              <input
                                type="checkbox"
                                checked={allInColumnSelected}
                                onChange={() => toggleSelectGroup(cells)}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                title={`Sélectionner la colonne ${column}`}
                              />
                              <span className="text-xs font-semibold text-gray-600">Colonne {column}</span>
                              <span className="text-[11px] text-gray-400 ml-auto">
                                {cells.length} étag.
                              </span>
                            </div>

                            {/* Ajouter par le haut : le bouton est là où la
                                planche se pose, la pile se lisant de haut en bas. */}
                            <button
                              onClick={() => handleAddShelf(Number(aisle), column, cells)}
                              className="w-full mb-1.5 min-h-[48px] text-xs font-medium text-gray-500
                                border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 hover:text-purple-600"
                              title={`Ajouter une étagère au-dessus de la colonne ${column}`}
                            >
                              + étagère en haut
                            </button>

                            <div>
                              {cells.map((loc, i) => {
                                const productCount = new Set(loc.assignments?.map(a => a.lot?.product?.id)).size;
                                const isExpanded = expandedLocation === loc.id;
                                const isSelected = selected.has(loc.id);

                                return (
                                  <div key={loc.id}>
                                    <div className={`w-full p-2 rounded-lg border text-left transition-colors ${
                                      isSelected ? 'ring-2 ring-purple-400 ' : ''
                                    }${
                                      !loc.active ? 'bg-red-50 border-red-200 opacity-50' :
                                      productCount > 0 ? 'bg-green-50 border-green-200 hover:bg-green-100' :
                                      'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                    }`}>
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleSelect(loc.id)}
                                          className="w-3.5 h-3.5 shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="shrink-0 px-1.5 py-0.5 rounded bg-white border text-[11px] font-bold text-gray-600">
                                          É{loc.shelf}
                                        </span>
                                        <button
                                          onClick={() => setExpandedLocation(isExpanded ? null : loc.id)}
                                          className="min-w-0 flex-1 text-left"
                                          title={`${loc.label} — voir le contenu`}
                                        >
                                          <span className="block font-mono text-[11px] text-gray-500 truncate">
                                            {loc.label}
                                          </span>
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setEditing(loc); }}
                                          className="shrink-0 p-1 text-gray-400 hover:text-purple-600 rounded transition-colors"
                                          title={`Modifier ${loc.label}`}
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handlePrintOne(loc); }}
                                          disabled={printing}
                                          className="shrink-0 p-1 text-gray-400 hover:text-green-600 rounded transition-colors"
                                          title={`Imprimer ${loc.label}`}
                                        >
                                          <Printer className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                      {loc.name && (
                                        <p className="text-xs font-medium text-gray-700 mt-1 ml-5 truncate" title={loc.name}>
                                          {loc.name}
                                        </p>
                                      )}
                                      <p className="text-[11px] text-gray-500 mt-0.5 ml-5">
                                        {productCount > 0 ? `${productCount} produit${productCount > 1 ? 's' : ''}` : 'Vide'}
                                      </p>
                                    </div>
                                    {isExpanded && loc.assignments && loc.assignments.length > 0 && (
                                      <div className="mt-1 p-2 bg-white border rounded-lg shadow-sm text-xs space-y-1">
                                        {loc.assignments.map(a => (
                                          <div key={a.id} className="flex justify-between items-center">
                                            <span className="truncate">{a.lot?.product?.name || 'Produit'}</span>
                                            <span className="font-medium ml-2 shrink-0">{a.quantity}</span>
                                          </div>
                                        ))}
                                        {/* Sans ce bouton, un emplacement dont le contenu
                                            est devenu faux restait indéracinable : la
                                            suppression le refuse tant qu'il contient
                                            quelque chose, et rien ne permettait de le vider. */}
                                        <button
                                          onClick={() => handleClearLocation(loc)}
                                          className="w-full mt-1 min-h-[48px] text-xs font-semibold text-orange-700
                                            bg-orange-50 border border-orange-200 rounded hover:bg-orange-100"
                                        >
                                          Vider cet emplacement
                                        </button>
                                      </div>
                                    )}

                                    {/* Entre deux étagères : la nouvelle prend le
                                        numéro juste au-dessus de celle du dessous,
                                        donc elle comble un trou s'il y en a un, et
                                        ne pousse que ce qu'il faut sinon. */}
                                    {i < cells.length - 1 && (
                                      <InsertShelfBar
                                        label={`Intercaler entre É${cells[i + 1].shelf} et É${loc.shelf}`}
                                        onClick={() => handleInsertShelf(
                                          Number(aisle), column, cells[i + 1].shelf + 1,
                                        )}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Sous la plus basse : la nouvelle planche prend son
                                numéro et pousse toute la colonne d'un cran. */}
                            <InsertShelfBar
                              label="Intercaler tout en bas"
                              onClick={() => handleInsertShelf(
                                Number(aisle), column, cells[cells.length - 1].shelf,
                              )}
                            />
                          </div>
                        );
                      })}

                      <button
                        onClick={() => handleAddColumn(Number(aisle), locs)}
                        className="shrink-0 w-32 min-h-[80px] mt-6 text-xs font-medium text-gray-500
                          border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 hover:text-purple-600"
                        title={`Ajouter une colonne à l'allée ${aisle}`}
                      >
                        + colonne
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      </div>
      </div>

      {showTrash && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowTrash(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Undo2 className="w-5 h-5 text-amber-700" />
                Suppressions annulables
              </h3>
              <button onClick={() => setShowTrash(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {trash.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6">Rien à annuler.</p>
              )}
              {trash.map((batch) => (
                <div key={batch.deleted_at} className="border rounded-lg p-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">
                      {batch.count} emplacement{batch.count > 1 ? 's' : ''}
                      <span className="text-gray-500 font-normal">
                        {' '}— allée{batch.aisles.length > 1 ? 's' : ''} {batch.aisles.join(', ')}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Supprimé le {new Date(batch.deleted_at).toLocaleString('fr-BE', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                    <p className="text-[11px] text-gray-400 font-mono truncate" title={batch.labels.join(' · ')}>
                      {batch.labels.slice(0, 6).join(' · ')}{batch.labels.length > 6 ? ' …' : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRestore(batch)}
                    disabled={batchActing}
                    className="shrink-0 px-3 py-2 text-xs font-semibold text-white bg-[#4a5c3a] hover:bg-[#3f5132] rounded-lg disabled:opacity-50"
                  >
                    {batchActing ? '…' : 'Restaurer'}
                  </button>
                </div>
              ))}
            </div>

            <div className="px-5 py-3 border-t text-xs text-gray-500">
              Une suppression reste annulable <strong>30 jours</strong>. La restauration rend
              le QR d'origine : les étiquettes déjà collées répondent de nouveau au scan.
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <BatchDeleteDialog
          locations={locations.filter((l) => selected.has(l.id))}
          working={batchActing}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleBatchDelete}
        />
      )}

      {editing && (
        <LocationEditModal
          // Remonte le formulaire quand on change d'emplacement : sans clé, un
          // second emplacement ouvert sans fermer le premier garderait les
          // valeurs du premier tout en enregistrant sur le second.
          key={editing.id}
          location={editing}
          onClose={() => setEditing(null)}
          onSave={handleSaveLocation}
          onDelete={() => handleDeleteOne(editing)}
          onPrint={() => handlePrintOne(editing)}
        />
      )}
    </div>
  );
}

/**
 * Confirmation de suppression en lot.
 *
 * Elle existe parce qu'une suppression d'emplacements est irréversible ET
 * silencieuse : rien ne dit combien on efface, ni ce qu'on perd. Elle énonce
 * donc trois choses que l'ancien bouton taisait — le nombre, les allées
 * touchées, et le fait que les QR meurent avec les emplacements, ce qui rend
 * mortes les étiquettes déjà collées.
 *
 * Le bouton destructeur est délibérément à l'opposé du bouton qui a ouvert
 * cette fenêtre : un second clic au même endroit ne doit rien pouvoir détruire.
 */
function BatchDeleteDialog({
  locations,
  working,
  onCancel,
  onConfirm,
}: {
  locations: StorageLocation[];
  working: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const parAllee = new Map<number, { total: number; nom: string | null }>();
  let avecStock = 0;
  for (const l of locations) {
    const e = parAllee.get(l.aisle) ?? { total: 0, nom: l.aisle_name ?? null };
    e.total += 1;
    parAllee.set(l.aisle, e);
    if ((l.assignments?.length ?? 0) > 0) avecStock += 1;
  }
  const allees = Array.from(parAllee.entries()).sort(([a], [b]) => a - b);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-red-600" />
          <h3 className="font-bold text-gray-900">
            Supprimer {locations.length} emplacement{locations.length > 1 ? 's' : ''} ?
          </h3>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Ce qui sera supprimé</p>
            <ul className="space-y-0.5">
              {allees.map(([num, e]) => (
                <li key={num} className="flex justify-between">
                  <span>{e.nom ? `${e.nom} (allée ${num})` : `Allée ${num}`}</span>
                  <span className="font-semibold">{e.total} emplacement{e.total > 1 ? 's' : ''}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-gray-600">
            Les QR de ces emplacements disparaissent avec eux : les étiquettes déjà collées
            deviendront muettes au scan. Recréer les mêmes adresses ne les fera pas revivre —
            il faudra réimprimer.
          </p>

          {avecStock > 0 && (
            <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              {avecStock} emplacement{avecStock > 1 ? 's contiennent' : ' contient'} encore de la
              marchandise. Le serveur refusera la suppression tant qu'ils ne seront pas vidés.
            </p>
          )}

          <p className="text-red-700 font-medium">Cette action est irréversible.</p>
        </div>

        {/* Annuler à gauche, destruction à droite : le clic qui vient d'ouvrir
            cette fenêtre ne peut pas tomber sur le bouton rouge. */}
        <div className="px-5 py-4 border-t flex items-center justify-between gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-gray-50"
            autoFocus
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={working}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
          >
            {working ? 'Suppression…' : `Supprimer ${locations.length} emplacement${locations.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Point d'insertion entre deux étagères.
 *
 * Discret par défaut — il y en a un dans chaque interstice — mais avec une zone
 * de clic pleine hauteur, sinon il devient invisible au doigt sur la caisse.
 */
function InsertShelfBar({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className="w-full my-1.5 h-6 flex items-center gap-1.5 text-gray-300 hover:text-purple-600 transition-colors"
    >
      <span className="flex-1 border-t border-dashed border-current" />
      <span className="px-1 text-sm font-bold leading-none">+</span>
      <span className="flex-1 border-t border-dashed border-current" />
    </button>
  );
}

/**
 * Édition d'un emplacement : son adresse, son nom, sa note.
 *
 * L'adresse (allée·colonne·étagère) reste faite de nombres parce que c'est elle
 * qui ordonne le parcours dans la réserve ; les mots libres viennent en plus,
 * jamais à la place. Déplacer un emplacement change son code mais garde son QR :
 * l'étiquette déjà collée continue de mener au bon endroit.
 */
function LocationEditModal({
  location,
  onClose,
  onSave,
  onDelete,
  onPrint,
}: {
  location: StorageLocation;
  onClose: () => void;
  onSave: (form: { aisle: number; column: number; shelf: number; name: string; notes: string; active: boolean }) => Promise<void>;
  onDelete: () => void;
  onPrint: () => void;
}) {
  const [form, setForm] = useState({
    aisle: location.aisle,
    column: location.column,
    shelf: location.shelf,
    name: location.name ?? '',
    notes: location.notes ?? '',
    active: location.active,
  });
  const [saving, setSaving] = useState(false);

  const moved =
    form.aisle !== location.aisle || form.column !== location.column || form.shelf !== location.shelf;
  const newLabel = `A${String(form.aisle).padStart(2, '0')}.C${String(form.column).padStart(2, '0')}.E${String(form.shelf).padStart(2, '0')}`;
  const hasStock = (location.assignments?.length ?? 0) > 0;

  const submit = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-900">
            Modifier {location.label}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Adresse</label>
            <div className="flex items-center gap-2">
              {([
                ['aisle', 'Allée'],
                ['column', 'Colonne'],
                ['shelf', 'Étagère'],
              ] as const).map(([key, label]) => (
                <div key={key} className="flex-1">
                  <input
                    type="number"
                    min="1"
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: Math.max(1, parseInt(e.target.value) || 1) }))}
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                  <p className="text-[11px] text-gray-400 mt-0.5 text-center">{label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Code : <span className="font-mono font-bold">{newLabel}</span>
            </p>
            {moved && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                L'étiquette collée mènera toujours ici (le QR ne change pas), mais elle affichera
                l'ancien code {location.label} : réimprimez-la après l'enregistrement.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nom (facultatif)</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Bac à œufs, Palette du fond…"
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Note (facultatif)</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            Emplacement utilisable
          </label>
          {!form.active && hasStock && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Cet emplacement contient de la marchandise : désactivé, son QR ne répondra plus au scan.
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t flex items-center gap-2">
          <button
            onClick={onDelete}
            className="px-3 py-2 text-sm text-red-700 hover:bg-red-50 border border-red-200 rounded-lg"
          >
            Supprimer
          </button>
          <button
            onClick={onPrint}
            className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 border rounded-lg"
          >
            Imprimer
          </button>
          <div className="flex-1" />
          <button onClick={onClose} className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
